import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmationModal } from "@/components/ConfirmationModal";
import { Badge } from "@/components/ui/badge";
import { EditDialog, type FieldDef } from "@/components/resource-page";
import type { TranslationKey } from "@/constants/i18n";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { AddButton } from "@/components/ui/list-section";
import { Loading } from "@/components/ui/loading";
import { SkeletonLines } from "@/components/ui/skeleton";
import { useRecordParam } from "@/hooks/use-record-param";
import { api, errorMessage, ifMatch } from "@/services/api";
import { listResource } from "@/utils/geo";
import { cn } from "@/utils/utils";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

export interface PromoCode {
  id: number;
  code: string;
  valid_until: string;
  description: string | null;
  /** Sent back as If-Match on a save or a delete, so one over someone else's is refused. */
  updated_at?: string;
}

const LIST_PAGE = 10;

/**
 * Whether the code has stopped working.
 *
 * Compared as dates, not as instants: a code valid until today is valid for the
 * whole of today.
 */
const isExpired = (validUntil: string) =>
  format(new Date(validUntil), "yyyy-MM-dd") < format(new Date(), "yyyy-MM-dd");

/** A code, when it stops working, and what it is for. */
const PROMO_FIELDS = (t: (key: TranslationKey) => string): FieldDef[] => [
  { name: "code", label: t("promo.code"), required: true },
  { name: "valid_until", label: t("promo.validUntil"), required: true, type: "date" },
  { name: "description", label: t("promo.description"), type: "textarea" },
];

/** The Promo Codes section as designed: a plain list, Edit / Delete, add; five more per scroll. */
export function PromoCodesSection() {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<PromoCode | null>(null);
  // Expired codes are noise most days; they stay a click away.
  const [showExpired, setShowExpired] = useState(false);

  // Ten at a time; the next ten when the list is scrolled to its end.
  const codes = useInfiniteQuery({
    queryKey: ["/promo-codes", "infinite", showExpired],
    queryFn: ({ pageParam, signal }) =>
      listResource<PromoCode>(
        "/promo-codes",
        { page: pageParam, size: LIST_PAGE, active_only: !showExpired },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/promo-codes"] });
  const remove = useMutation({
    mutationFn: (row: { id: number; updated_at?: string }) =>
      api.delete(`/promo-codes/${row.id}`, ifMatch(row)),
    onSuccess: () => {
      toast.success(t("promo.deleted"));
      void invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t("promo.deleteFailed"))),
  });

  const rows = codes.data?.pages.flatMap((page) => page.items) ?? [];
  const { editing, setEditing, loading } = useRecordParam<PromoCode>(
    "edit",
    "promo-code",
    "/promo-codes",
    rows,
  );

  // A record is read when it is opened, so between the click and the answer
  // there is nothing to fill the form with. The same sheet, empty, rather than
  // the list sitting there as though the click had missed.
  const editor = loading ? (
    <Modal
      open
      onClose={() => setEditing(null)}
      title={t("rp.editSingular").replace("{singular}", t("promo.code"))}
    >
      <Loading busy subject={t("promo.code")}>
        <SkeletonLines rows={3} />
      </Loading>
    </Modal>
  ) : editing !== null ? (
    <EditDialog
      singular={t("promo.code")}
      endpoint="/promo-codes"
      fields={PROMO_FIELDS(t)}
      initial={
        editing === "new"
          ? {}
          : {
              code: editing.code,
              valid_until: editing.valid_until,
              description: editing.description ?? "",
            }
      }
      id={editing === "new" ? null : editing.id}
      stamp={editing === "new" ? null : (editing.updated_at ?? null)}
      onClose={() => setEditing(null)}
      onSaved={() => {
        setEditing(null);
        void invalidate();
      }}
    />
  ) : null;

  return (
    <div>
      {/* Title and what the screen is for on the left, the one thing it is for
          on the right. No second heading repeating the first, and no count: the
          list below says how many there are by being it. */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h2 className="text-title-2 text-heading">{t("promo.title")}</h2>
          <p className="text-fg-muted text-callout mt-1">{t("promo.intro")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="text-fg-muted text-subhead flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="accent-accent size-4"
              checked={showExpired}
              onChange={(event) => setShowExpired(event.target.checked)}
            />
            {t("promo.showExpired")}
          </label>
          {allow("add__promo_code") ? (
            <AddButton onClick={() => setEditing("new")}>{t("promo.add")}</AddButton>
          ) : null}
        </div>
      </div>
      <ul className="mt-6">
        {codes.isPending ? (
          <li className="py-2">
            <SkeletonLines rows={6} />
          </li>
        ) : null}
        {rows.map((row) => (
          <li
            key={row.id}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 py-3 sm:grid-cols-[minmax(0,1fr)_11rem_auto]"
          >
            <span className="col-span-2 min-w-0 sm:col-span-1">
              <span className="flex min-w-0 items-center gap-2">
                <span className="text-fg text-callout truncate font-medium">{row.code}</span>
                {/* A code that has stopped working looks exactly like one that
                    still works; the date beside it is the only thing saying
                    otherwise, and that is a subtraction to do by eye on a list
                    that shows both together. */}
                {isExpired(row.valid_until) ? (
                  <Badge tone="danger" className="shrink-0">
                    {t("promo.expired")}
                  </Badge>
                ) : null}
              </span>
              <span
                className={cn(
                  "text-subhead block truncate leading-[18px]",
                  row.description ? "text-fg-muted" : "text-fg-subtle italic",
                )}
              >
                {row.description || t("promo.noDescription")}
              </span>
            </span>
            <span className="text-fg-muted text-subhead leading-[18px] whitespace-nowrap">
              {t("promo.validUntil")}: {format(new Date(row.valid_until), "MM/dd/yyyy")}
            </span>
            <span className="text-subhead flex shrink-0 items-center gap-1 justify-self-end">
              {allow("change__promo_code") ? (
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.edit")}
                </button>
              ) : null}
              {allow("delete__promo_code") ? (
                <button
                  type="button"
                  onClick={() => setDeleting(row)}
                  className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 font-medium transition-colors"
                >
                  {t("common.delete")}
                </button>
              ) : null}
            </span>
          </li>
        ))}
        {!codes.isPending && rows.length === 0 ? (
          <li className="text-fg-muted text-callout py-6 text-center">{t("promo.none")}</li>
        ) : null}
      </ul>

      {/* Ten at a time, asked for rather than taken: the page scrolls as one,
          so a list that loaded more as it was scrolled would never let the
          bottom of the page arrive. */}
      {codes.hasNextPage ? (
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => void codes.fetchNextPage()}
            loading={codes.isFetchingNextPage}
            loadingLabel={t("common.loading")}
          >
            {t("common.loadMore")}
          </Button>
        </div>
      ) : null}

      {editor}

      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
          setDeleting(null);
        }}
        title={t("common.confirmDeleteTitle").replace("{model}", t("promo.code"))}
        message={deleting ? t("promo.confirmDelete").replace("{code}", deleting.code) : ""}
      />
    </div>
  );
}
